import {
  Button,
  Col,
  DatePicker,
  Divider,
  Image,
  Input,
  message,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd"
import React, { useEffect, useRef, useState } from "react"
import {
  ArrowDownOutlined,
  CloseCircleFilled,
  CloseOutlined,
  DatabaseFilled,
  EyeInvisibleOutlined,
  FileOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import { collection, getDocs, onSnapshot, query, Timestamp, where } from "firebase/firestore"
import dayjs from "dayjs"
import * as XLSX from "xlsx"
import Highlighter from "react-highlight-words"
import { useCollection } from "../../hooks/useCollection"
import { useAuthContext } from "../../hooks/useAuthContext"
import { db } from "../../firebase/config"
import InvoiceOrders from "../Accounting/Clients/Helper/InvoiceOrders"
import InvoicePDFPreview from "../Accounting/Invoices/Helper/InvoicePDFPreview"
import { loadingSpinWithBg } from "../../components/Elements/LoadingSpinner"
import PageHeader from "../../components/Elements/PageHeader"
import { InteractivePaymentStatus } from "../../components/Payment/InteractivePaymentStatus"

const { Link, Text } = Typography
const { RangePicker } = DatePicker
const { Option } = Select

export default function AllProformas() {
  // get data for manufacturer list
  const { data: manufacturerList, loading: manufacturerLoading } = useCollection("manufacturers")

  // user declaration
  const { user } = useAuthContext()

  // Set default dates to the current month using dayjs
  const startOfMonth = dayjs().startOf("month")
  const endOfMonth = dayjs().endOf("month")
  const startTimestamp = Timestamp.fromDate(startOfMonth.toDate())
  const endTimestamp = Timestamp.fromDate(endOfMonth.toDate())

  // search range state (Firebase Timestamps)
  const [startDate, setStartDate] = useState(startTimestamp)
  const [endDate, setEndDate] = useState(endTimestamp)

  // selected client for search
  const [searchClient, setSearchClient] = useState("all")

  // all fetched invoices
  const [allInvoices, setAllInvoices] = useState([])

  // table loading
  const [tableLoading, setTableLoading] = useState(false)

  // clients list state
  const [clientList, setClientList] = useState([])
  const [clientsLoading, setClientsLoading] = useState(false)

  // global loading
  const [globalLoading, setGloabalLoading] = useState(false)

  const [searchText, setSearchText] = useState("")
  const [searchedColumn, setSearchedColumn] = useState("")
  const searchInput = useRef(null)

  // NEW: state to hold the selected rows for export
  const [selectedRows, setSelectedRows] = useState([])

  // State to track filtered data for accurate totals
  const [filteredInvoices, setFilteredInvoices] = useState([])

  // handles the available client list
  useEffect(() => {
    setClientsLoading(true)
    let ref = collection(db, "clients")
    let q = query(ref, where("user", "==", user.uid))

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        results.push({ ...doc.data(), id: doc.id })
      })
      setClientList(results)
      setClientsLoading(false)
    })
    return () => unsubscribe()
  }, [user])

  // Initialize filtered invoices when allInvoices changes
  useEffect(() => {
    setFilteredInvoices(allInvoices)
  }, [allInvoices])

  // returns global loading indicator if needed
  const returnGlobalLoading = () => {
    return <>{loadingSpinWithBg(75, "Generating File...")}</>
  }

  if (globalLoading) {
    return returnGlobalLoading()
  }

  // function to download the Excel file; export selected rows if any, otherwise export all invoices
  const downloadExcel = () => {
    // Determine which data to export: selected rows or all invoices
    const exportData = selectedRows.length > 0 ? selectedRows : allInvoices

    // Create a new array with only the desired fields
    const filteredData = exportData.map((item) => ({
      "Invoice Number": item.docNumber,
      Date: dayjs.unix(item.documentDate.seconds).format("DD.MM.YYYY"),
      Client: item.client.client,
      Manufacturer: item.manufacturer.company,
      "Neto (eur)": item.totalData.totalNetoSum,
      "VAT (eur)": item.totalData.totalVat,
      "Total (eur)": item.totalData.totalSum,
    }))

    // Create a new workbook and worksheet with the filtered data
    let wb = XLSX.utils.book_new()
    let ws = XLSX.utils.json_to_sheet(filteredData)
    XLSX.utils.book_append_sheet(wb, ws, "Selection")
    XLSX.writeFile(wb, "selection.xlsx")
  }

  // on change select for clients
  const clientSearchSelect = (value) => {
    setSearchClient(value)
  }

  // on datepicker change for export range using dayjs
  const onDateChange = (dates, dateStrings) => {
    if (dates) {
      const [start, end] = dates
      setStartDate(Timestamp.fromDate(start.toDate()))
      setEndDate(Timestamp.fromDate(end.endOf("day").toDate()))
    }
  }

  // gets invoices based on search criteria
  const getInvoices = async () => {
    setTableLoading(true)
    try {
      const ref = collection(db, "proformas")
      let constraints = [where("userId", "==", user.uid)]

      if (startDate && endDate) {
        constraints.push(where("documentDate", ">=", startDate))
        constraints.push(where("documentDate", "<=", endDate))
      }
      if (searchClient !== "all") {
        constraints.push(where("client.id", "==", searchClient))
      }
      const q = query(ref, ...constraints)
      let results = []
      const querySnapshot = await getDocs(q)
      querySnapshot.forEach((doc) => {
        results.push({ ...doc.data(), id: doc.id })
      })
      setAllInvoices(results)
      setTableLoading(false)
    } catch (error) {
      message.error(`Error: ${error.message}`)
      console.log(error.message)
      setGloabalLoading(false)
    }
  }

  // Calculate totals for the table footer
  const calculateTotals = (invoices) => {
    const totalNetoSum = invoices.reduce(
      (total, invoice) => total + (invoice.totalData?.totalNetoSum || 0),
      0,
    )
    const totalVat = invoices.reduce(
      (total, invoice) => total + (invoice.totalData?.totalVat || 0),
      0,
    )
    const totalSum = invoices.reduce(
      (total, invoice) => total + (invoice.totalData?.totalSum || 0),
      0,
    )
    return { totalNetoSum, totalVat, totalSum }
  }

  // Handle table changes (including filtering, sorting, pagination)
  const handleTableChange = (pagination, filters, sorter, extra) => {
    // extra.currentDataSource contains the filtered data after all filters are applied
    if (extra && extra.currentDataSource) {
      setFilteredInvoices(extra.currentDataSource)
    } else {
      // Fallback to allInvoices if currentDataSource is not available
      setFilteredInvoices(allInvoices)
    }
  }

  let manufacturerFilter =
    manufacturerList &&
    manufacturerList.map((item) => ({
      text: item.company,
      value: item.company,
    }))

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters) => {
    clearFilters()
    setSearchText("")
  }

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}>
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : "",
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  })

  const defaultColumns = [
    {
      title: "Logo",
      dataIndex: "InvoiceLogo",
      width: "3%",
      align: "left",
      render: (value) => {
        if (value) {
          return <Image width={50} alt="invoice logo" preview={false} src={value} />
        } else {
          return <Text>CMS</Text>
        }
      },
    },
    {
      title: "Number",
      dataIndex: "docNumber",
      width: "5%",
      align: "left",
      ...getColumnSearchProps("docNumber"),
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value} - <CloseCircleFilled />
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Series",
      dataIndex: "docPrefix",
      width: "5%",
      align: "left",
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Date",
      dataIndex: ["documentDate", "seconds"],
      width: "10%",
      align: "left",
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {dayjs.unix(value).format("DD.MM.YYYY")}
          </Text>
        ) : (
          <Text>{dayjs.unix(value).format("DD.MM.YYYY")}</Text>
        ),
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "left",
      width: "15%",
      sorter: (a, b) => a.client.client.localeCompare(b.client.client),
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Manufacturer",
      dataIndex: ["manufacturer", "company"],
      align: "left",
      width: "15%",
      sorter: (a, b) => a.manufacturer.company.localeCompare(b.manufacturer.company),
      filters: manufacturerFilter,
      onFilter: (value, record) => record.manufacturer.company === value,
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Neto (eur)",
      dataIndex: ["totalData", "totalNetoSum"],
      width: "5%",
      align: "right",
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Vat (eur)",
      dataIndex: ["totalData", "totalVat"],
      width: "5%",
      align: "right",
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Total (eur)",
      dataIndex: ["totalData", "totalSum"],
      width: "5%",
      align: "right",
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Download",
      dataIndex: "downloadUrl",
      width: "5%",
      align: "center",
      render: (value, record) => {
        if (record.downloadUrl) {
          return (
            <>
              <Link href={value}>
                <ArrowDownOutlined />
              </Link>
            </>
          )
        } else {
          return <CloseOutlined />
        }
      },
    },
    {
      title: "Orders",
      dataIndex: "orderReferenceId",
      width: "10%",
      align: "right",
      render: (value) => <InvoiceOrders orderReferenceIds={value} />,
    },
    {
      title: "Payment",
      dataIndex: "paymentStatus",
      width: "5%",
      align: "center",
      filters: [
        { text: "Paid", value: 2 },
        { text: "Partial Paid", value: 1 },
        { text: "Not Paid", value: 0 },
      ],
      onFilter: (value, record) => record.paymentStatus === value,
      render: (text, record) => (
        <InteractivePaymentStatus documentType="proforma" record={record} />
      ),
    },
    {
      title: "Show",
      dataIndex: "downloadUrl",
      width: "5%",
      align: "center",
      render: (value) => {
        if (value) {
          return <InvoicePDFPreview pdfUrl={value} />
        } else {
          return <EyeInvisibleOutlined style={{ color: "#8c8c8c" }} />
        }
      },
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader text="My Invoices" icon={<DatabaseFilled />} />
        </Col>
        <Col span={24}>
          <Divider orientation="left">Selection</Divider>
          <Space>
            <RangePicker
              format="DD.MM.YYYY"
              onChange={onDateChange}
              defaultValue={[startOfMonth, endOfMonth]}
            />
            <Select
              showSearch
              placeholder="All clients selected"
              optionFilterProp="children"
              style={{ width: 250 }}
              defaultValue="all"
              loading={clientsLoading}
              onChange={clientSearchSelect}
              filterOption={(input, option) =>
                option?.children.toLowerCase().includes(input.toLowerCase())
              }>
              <Option key={1} value="all">
                All Clients
              </Option>
              {clientList &&
                clientList.map((client) => (
                  <Option key={client.id} value={client.id}>
                    {client.client}
                  </Option>
                ))}
            </Select>
            <Button onClick={getInvoices}>Search</Button>
          </Space>
        </Col>
        <Col span={24}>
          <Divider orientation="left">Exports</Divider>
          <Space>
            <Button onClick={downloadExcel}>
              <FileOutlined /> Export to XLSX
            </Button>
          </Space>
        </Col>
        <Col span={24}>
          <Table
            size="small"
            rowKey={(record) => record.id}
            bordered
            dataSource={allInvoices}
            loading={tableLoading || manufacturerLoading}
            columns={defaultColumns}
            onChange={handleTableChange}
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
            rowSelection={{
              columnWidth: "3%",
              onChange: (selectedRowKeys, selectedRows) => setSelectedRows(selectedRows),
            }}
            footer={() => {
              const { totalNetoSum, totalVat, totalSum } = calculateTotals(filteredInvoices)
              return (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Space>
                    <Tag color="blue">Total Neto (eur): {totalNetoSum.toFixed(2)}</Tag>
                    <Tag color="blue">Total Vat (eur): {totalVat.toFixed(2)}</Tag>
                    <Tag color="blue">Total (eur): {totalSum.toFixed(2)}</Tag>
                  </Space>
                </div>
              )
            }}
          />
        </Col>
      </Row>
    </div>
  )
}
